// src/components/OrgPreview/HierarchyNode.js
import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { IconBuilding, IconUsers, IconUser } from '@tabler/icons-react';

/**
 * Custom ReactFlow node for displaying hierarchy levels
 * Supports both vertical (TB) and horizontal (LR) layouts via dynamic handle positions
 * Unit leaders are displayed inside the card
 */
const HierarchyNode = memo(({ data, sourcePosition, targetPosition }) => {
    const { label, level, childCount, userCount, unitLeaders = [] } = data;

    // Determine handle positions from props (set by Dagre layout) or use defaults
    const targetPos = targetPosition || Position.Top;
    const sourcePos = sourcePosition || Position.Bottom;

    // Different styling based on level - using "Ebene X" naming
    const getLevelConfig = (lvl) => {
        const configs = {
            0: {
                icon: IconBuilding,
                className: 'level-company',
                label: 'Ebene 0',
            },
            1: {
                icon: IconUsers,
                className: 'level-division',
                label: 'Ebene 1',
            },
            2: {
                icon: IconUsers,
                className: 'level-department',
                label: 'Ebene 2',
            },
            3: {
                icon: IconUser,
                className: 'level-team',
                label: 'Ebene 3',
            },
        };
        // For levels beyond 3, generate dynamically
        if (lvl > 3) {
            return {
                icon: IconUser,
                className: 'level-team',
                label: `Ebene ${lvl}`,
            };
        }
        return configs[lvl] || configs[3];
    };

    const config = getLevelConfig(level);
    const Icon = config.icon;

    // Show source handle if there are children
    const hasOutgoingConnections = childCount > 0;

    return (
        <div className={`org-hierarchy-node ${config.className}`}>
            {/* Target handle - where edges come in (from parent) */}
            {level > 0 && (
                <Handle
                    type="target"
                    position={targetPos}
                    className="org-handle"
                />
            )}

            <div className="hierarchy-node-content">
                <div className="hierarchy-icon">
                    <Icon size={level === 0 ? 22 : 18} stroke={1.5} />
                </div>
                <div className="hierarchy-info">
                    <span className="hierarchy-name">{label}</span>
                    <div className="hierarchy-meta-row">
                        {childCount > 0 && (
                            <span className="hierarchy-meta">
                                {childCount} {childCount === 1 ? 'Untereinheit' : 'Untereinheiten'}
                            </span>
                        )}
                        {userCount > 0 && (
                            <span className="hierarchy-users">
                                <IconUser size={12} stroke={1.5} />
                                {userCount}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Unit Leaders displayed inside the card */}
            {unitLeaders.length > 0 && (
                <div className="hierarchy-leaders">
                    {unitLeaders.map((leader, index) => (
                        <div key={index} className="hierarchy-leader">
                            <div className="leader-avatar">
                                <IconUser size={14} stroke={1.5} />
                            </div>
                            <div className="leader-info">
                                <span className="leader-name">
                                    {`${leader.firstname} ${leader.lastname}`.trim() || 'Unbekannt'}
                                </span>
                                <span className="leader-email">{leader.email}</span>
                                {leader.dashboard && (
                                    <span className="leader-dashboard">{leader.dashboard}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Source handle - where edges go out (to children) */}
            {hasOutgoingConnections && (
                <Handle
                    type="source"
                    position={sourcePos}
                    className="org-handle"
                />
            )}
        </div>
    );
});

HierarchyNode.displayName = 'HierarchyNode';

export default HierarchyNode;
